/**
 * 繁體中文語系檔案
 */

export default {
  common: {
    confirm: '確認',
    cancel: '取消',
    save: '儲存',
    delete: '刪除',
    edit: '編輯',
    close: '關閉',
    loading: '載入中...',
    success: '成功',
    error: '錯誤',
    warning: '警告',
    info: '資訊',
    search: '搜尋',
    refresh: '刷新',
    download: '下載',
    upload: '上傳',
    export: '匯出',
    import: '匯入',
    settings: '設定',
    logout: '登出',
    login: '登入',
    yes: '是',
    no: '否',
  },

  nav: {
    dashboard: '儀表板',
    devices: '設備列表',
    settings: '系統設定',
    logout: '登出',
    language: '語言',
  },

  auth: {
    loginTitle: '登入系統',
    username: '使用者名稱',
    password: '密碼',
    customerCode: '客戶代碼',
    customerCodePlaceholder: '請輸入客戶代碼',
    passwordPlaceholder: '請輸入密碼',
    loginButton: '登入',
    loggingIn: '登入中...',
    loginSuccess: '登入成功',
    loginFailed: '登入失敗',
    invalidCredentials: '帳號或密碼錯誤',
    networkError: '無法連接到服務器，請檢查網路連線',
    unknownError: '登入失敗，請稍後再試',
    testAccount: '測試帳號：admin / admin123',
    platformName: '太陽能監控系統',
    version: 'SolarSDGs IoT Platform v1.0.0',
    copyright: '© 2025 SolarSDGs. All rights reserved.',
  },

  dashboard: {
    title: '太陽能監控儀表板',
    deviceInfo: '設備資訊',
    deviceId: '設備 ID',
    deviceName: '設備名稱',
    deviceType: '設備類型',
    location: '位置',
    status: '狀態',
    online: '已連線',
    offline: '未連線',
    lastUpdate: '最後更新',
    dataCount: '數據總數',
    records: '筆',
    dataInterval: '偵測頻率',
    seconds: '秒',
    networkType: '網路類型',
    unknown: '未知',

    // 功率數據
    powerData: '功率數據',
    generation: '發電功率',
    loadA: '負載 A',
    loadP: '負載 P',
    unit: {
      watt: 'mW',
      percent: '%',
    },

    // 效率數據
    efficiency: '效率數據',
    pagEfficiency: '負載 A 效率',
    pgpEfficiency: '負載 P 效率',

    // GPS 地圖
    map: {
      title: '設備位置',
      latitude: '緯度',
      longitude: '經度',
      altitude: '海拔',
      satellites: '衛星數量',
      noData: '無 GPS 數據',
      failed: '無法載入 GPS 位置',
      refresh: '刷新位置',
      loading: '載入中...',
      updateTime: '更新時間',
      device: '設備',
    },

    // 圖像
    images: {
      title: '最新圖像',
      rgb: 'RGB 圖像',
      thermal: '熱影像',
      capturedAt: '拍攝時間',
      fileSize: '檔案大小',
      clickToEnlarge: '點擊放大',
      noImage: '尚無圖像數據',
      hint: '設備每 10 分鐘自動上傳一次圖像',
      downloadLatest: '下載最新',
      batchDownload: '批量下載',
      batchDownloadTitle: '批量下載圖像',
      startTime: '開始時間',
      endTime: '結束時間',
      downloadZip: '下載 ZIP',
      downloadHint: '將下載該時段選定類型的圖像，打包成 ZIP 檔案',
      npzNotAvailable: 'NPY 溫度 RAW 尚未生成',
      selectAtLeastOneType: '請至少選擇一種類型',
      selectDateRange: '請選擇日期範圍',
      invalidDateRange: '開始日期不能晚於結束日期',
      downloading: '下載中...',
      downloadSuccess: '批量下載成功',
      downloadFailed: '下載失敗',
      loading: '載入中...',
      refresh: '刷新',
      close: '✕',
      cancel: '取消',
      confirm: '確認',
      staticDemo: '展示圖片（固定）',
      demoImage: '展示用圖片',
      npyRaw: 'NPY (溫度 RAW)',
    },

    // 相機控制
    camera: {
      title: '相機狀態',
      online: '在線',
      offline: '離線',
      lastCapture: '上次拍照',
      storage: '儲存空間',
      free: '可用',
      captureNow: '立即拍照',
      capturing: '拍照中...',
      captureFailed: '拍照失敗',
      loading: '載入相機狀態中...',
      noData: '尚無相機數據',
      offlineHint: '相機離線中，無法拍照',
    },

    // 數據下載
    dataDownload: {
      title: '下載資料',
      csvExport: '匯出 CSV',
      selectRange: '選擇時間範圍',
      downloading: '下載中...',
      success: '下載成功',
      failed: '下載失敗',
    },

    // Factor 配置
    factorConfig: {
      title: 'Factor 設定 (效率修正係數)',
      factorA: 'Factor A (PA修正)',
      factorP: 'Factor P (PP修正)',
      updating: '更新中...',
      updateButton: '更新 Factor',
      resetButton: '重置為 1.0',
      updateSuccess: 'Factor 更新成功',
      updateFailed: 'Factor 更新失敗',
      info: 'Factor 用於修正功率數據的效率計算',
      formulaPGA: '公式: PGA% = (PA × Factor_A - PG) / PG × 100',
      formulaPGP: '公式: PGP% = (PP × Factor_P - PG) / PG × 100',
    },

    // 圖表
    charts: {
      powerTrend: '功率趨勢圖',
      pagTrend: 'PGA 效率趨勢圖 (%)',
      pgpTrend: 'PGP 效率趨勢圖 (%)',
      weatherTrend: '氣象數據趨勢圖',
      panelTempTrend: '板溫趨勢圖（熱影像）',
      panelTemp: '板溫',
      skyTemp: '天空溫度',
      virtualIrradianceTrend: '日照（Open-Meteo 為準）',
      viEstimated: '影像估計（實驗）',
      viCalibrating: '影像估計校準中（需 20 個白天配對小時），目前先顯示 Open-Meteo',
      rainVisionTrend: '雨量（Open-Meteo ＋ 現場確認淋濕）',
      rainScore: '現場確認淋濕',
      rainMm: 'Open-Meteo 雨量',
      soilingVisionTrend: '表面汙染趨勢（影像）— 晴空幀，雨後自動重置乾淨基準',
      roiCalib: 'ROI 標定',
      soilingDiff: '抗汙效益 (REF−鍍膜)',
      brightness: '汙染指數',
      timeRangeOptions: {
        '1': '1 分鐘/點 (過去 60 分鐘)',
        '5': '5 分鐘/點 (過去 5 小時)',
        '10': '10 分鐘/點 (過去 10 小時)',
        '30': '30 分鐘/點 (過去 30 小時)',
        '60': '1 小時/點 (過去 60 小時)',
        '360': '6 小時/點 (過去 15 天)',
        '1440': '1 天/點 (過去 60 天)',
      },
      efficiencyTimeRange: {
        '60': '最近 1 小時',
        '180': '最近 3 小時',
        '360': '最近 6 小時',
        '720': '最近 12 小時',
        '1440': '最近 1 天',
        '4320': '最近 3 天',
        '10080': '最近 1 週',
      },
      downloadButton: '下載資料 (CSV)',
      downloadWeather: '下載氣象資料 (CSV)',
      downloadCsv: '下載 CSV',
      energyTrend: '總發電量（每區間 kWh，由 10 分鐘平均加總）',
      energyPg: 'PG 發電量',
      energyPa: 'PA 發電量',
      energyPp: 'PP 發電量',
      energyFilled: '內插補值格數',
      energyIntervalOptions: {
        '10': '10 分鐘/點（最近 10 小時）',
        '30': '30 分鐘/點（最近 30 小時）',
        '60': '1 小時/點（最近 60 小時）',
        '360': '6 小時/點（最近 15 天）',
        '1440': '1 天/點（最近 60 天）',
      },
      gainTrendAp: '總發電量增益 — AP 相對 REF（%）',
      gainTrendAr: '總發電量增益 — AR 相對 REF（%）',
      gainAp: 'AP 相對 REF',
      gainAr: 'AR 相對 REF',
      gainAxis: '增益 %',
      rearRefTrend: '背面參考片比值 — PP / PG（%，正面積塵 → 上升）',
      rearRefRatio: 'PP / PG',
      rearRefAxis: 'PP / PG %',
      soilingPowerTrend: '電性汙染 — 正面板相對背面參考片的發電損失',
      soilingPowerMeta: '每日 10–14 時 PG/PP、PA/PP · 雨後最高比值 = 0% · 虛線為影像汙染對照',
      lossPgPp: 'PG/PP 發電損失 %（REF 板）',
      lossPaPp: 'PA/PP 發電損失 %（AR 板）',
      lossAxis: '發電損失 %',
      soilIndexAxis: '影像汙染指數',
      apSoil: 'AP 汙染',
      arSoil: 'AR 汙染',
      refSoil: 'REF 汙染',
      soilAxis: '汙染程度 (R-B vs 自基準)',
    },

    // CSV 下載
    csvDownload: {
      title: '下載發電資料 (CSV)',
      weatherTitle: '下載氣象資料 (CSV)',
      startDate: '開始日期',
      endDate: '結束日期',
      hint: '將下載指定時間範圍內的所有原始數據（未經聚合）',
      weatherHint: '將下載指定時間範圍內的所有氣象原始數據（日照、雨量、風力）',
      cancel: '取消',
      confirm: '確認下載',
      downloading: '下載中...',
      success: '下載成功',
      failed: '下載失敗',
      selectDates: '請選擇開始和結束日期',
      invalidRange: '開始日期必須早於結束日期',
      rangeTooLong: '時間範圍不能超過 90 天',
      noData: '沒有可下載的數據',
      successCount: 'CSV 下載成功！共 {count} 筆資料',
    },

    // 氣象數據
    weather: {
      solarRadiation: '日照量',
      rainfall: '雨量監測',
      windPower: '風力發電',
      noData: '暫無數據',
      // 日照等級
      radiationLow: '日照微弱',
      radiationMedium: '日照適中',
      radiationHigh: '日照強烈',
      radiationVeryHigh: '日照極強',
      // 雨量
      totalRainfall: '累計雨量',
      rainfallRate: '雨量強度',
      rainDry: '無降雨',
      rainLight: '小雨',
      rainModerate: '中雨',
      rainHeavy: '大雨',
      rainTorrential: '暴雨',
      // 風力
      windStopped: '停機',
      windLow: '微風發電',
      windMedium: '正常發電',
      windHigh: '高效發電',
      windVeryHigh: '滿載發電',
      generating: '發電中',
      standby: '待機中',
    },
  },

  devices: {
    title: '選擇監控設備',
    subtitle: '請選擇要監控的太陽能發電設備',
    selectDevice: '請選擇設備',
    noDevices: '暫無可用設備',
    noDevicesHint: '請聯繫管理員添加設備',
    viewDashboard: '查看儀表板',
    loading: '載入設備列表中...',
    loadFailed: '載入設備列表失敗',
    retry: '重新載入',
    device: '設備',
    deviceType: '設備類型',
    lastSeen: '最後上線',
    updatedAt: '更新於',
    noData: '暫無數據',
    online: '在線',
    offline: '離線',
    solarSystem: '太陽能發電系統',
  },

  settings: {
    title: '系統設定',
    language: '語言設定',
    theme: '主題設定',
    notifications: '通知設定',
  },

  errors: {
    network: '網路連線失敗',
    unauthorized: '未授權，請重新登入',
    forbidden: '無權訪問此設備',
    notFound: '找不到資源',
    serverError: '伺服器錯誤',
    unknownError: '未知錯誤',
    loadFailed: '載入失敗',
    adminOnly: '您沒有訪問管理頁面的權限',
  },

  coating: {
    title: '鍍膜效能',
    navButton: '🧪 鍍膜效能',
    subtitle: 'AP(抗汙疏水) / AR(抗反射親水) / REF(無鍍膜) 三板對比分析',
    apGain: 'AP 增益 vs REF',
    arGain: 'AR 增益 vs REF',
    soilingAmplitude: '目前積塵幅度',
    cleaningIn: '建議清洗',
    cleaningDays: '{days} 天後',
    cleaningNow: '建議立即清洗',
    cleaningNone: '暫無累積趨勢',
    lifespan: '薄膜壽命趨勢',
    lifespanDegrading: '緩慢衰退中',
    lifespanStable: '無衰退跡象',
    lifespanInsufficient: '資料累積中',
    prCurrent: '目前 PR（REF 無鍍膜）',
    prChart: '每日 PR — 相對乾淨態（GPS + Open-Meteo 日照歸一化，虛線 = 80% 清洗門檻）',
    prThresholdNote: 'PR < 80% 建議清洗',
    prUnavailable: '日照資料暫不可用',
    valleyPoints: '谷底（週期最低增益日）',
    valleyLabel: '膜本質（谷底）',
    logCleaning: '記錄清洗',
    logCleaningConfirm: '確認登記一次人工清洗（現在時間）？之後的分析會把這天標為人工清洗事件。',
    baselineChart: '每日鍍膜增益 vs REF + 趨勢線（▼ 藍=降雨 橘=登記清洗 灰=未知；◆ 白=週期低點）',
    year: '年',
    yearsLeft: ' 年歸零',
    dataRange: '資料範圍',
    days: '天',
    resetEvents: '重置事件',
    loading: '分析計算中...',
  },

  messages: {
    saveSuccess: '儲存成功',
    saveFailed: '儲存失敗',
    deleteSuccess: '刪除成功',
    deleteFailed: '刪除失敗',
    updateSuccess: '更新成功',
    updateFailed: '更新失敗',
    confirmDelete: '確定要刪除嗎？',
    unsavedChanges: '有未儲存的變更',
  },

  titles: {
    login: 'SolarSDGs IoT - 登入',
    devices: 'SolarSDGs IoT - 選擇設備',
    dashboard: 'SolarSDGs IoT - 即時監控',
    coating: 'SolarSDGs IoT - 鍍膜效能',
    roi: 'SolarSDGs IoT - 三板 ROI 標定',
    admin: 'SolarSDGs IoT - 系統管理',
  },

  admin: {
    title: '系統管理',
    tabDevices: '📡 設備管理',
    tabUsers: '👥 使用者管理',
    deviceList: '設備列表',
    addDevice: '➕ 新增設備',
    deviceId: '設備 ID',
    deviceName: '設備名稱',
    status: '狀態',
    createdAt: '建立時間',
    actions: '操作',
    enabled: '啟用',
    disabled: '停用',
    enable: '啟用',
    disable: '停用',
    userList: '使用者列表',
    addUser: '➕ 新增使用者',
    account: '帳號',
    name: '名稱',
    accessibleDevices: '可訪問設備',
    allDevicesAdmin: '全部設備（管理員）',
    assignDevicesTip: '分配設備',
    editDevice: '編輯設備',
    newDevice: '新增設備',
    deviceIdPlaceholder: '例如: 6003',
    deviceNamePlaceholder: '例如: 南投埔里太陽能板',
    enableDevice: '啟用設備',
    editUser: '編輯使用者',
    newUser: '新增使用者',
    accountPlaceholder: '例如: user01',
    namePlaceholder: '例如: 張三',
    password: '密碼',
    passwordPlaceholder: '請輸入密碼',
    adminAccount: '管理員帳號（可訪問所有設備）',
    enableAccount: '啟用帳號',
    assignTitle: '分配設備',
    setAdmin: '設為管理員（可訪問所有設備）',
    unnamedDevice: '未命名設備',
    deviceUpdated: '設備更新成功',
    deviceCreated: '設備新增成功',
    userUpdated: '使用者更新成功',
    userCreated: '使用者新增成功',
    assignSuccess: '設備分配成功',
    opFailed: '操作失敗: {msg}',
    opFailedRetry: '操作失敗，請稍後再試',
    confirmToggleDevice: '確定要{action}設備 {id} 嗎？',
    confirmToggleUser: '確定要{action}使用者 {id} 嗎？',
    confirmLogout: '確定要登出嗎？',
  },

  roi: {
    title: '三板 ROI 標定',
    back: '← 儀表板',
    apLabel: 'AP（左板・抗汙疏水）',
    arLabel: 'AR（中板・抗反射親水）',
    refLabel: 'REF（右板・無鍍膜）',
    dupChannel: '⚠️ 通道重複指定，請確認每板對應不同通道',
    saveFailed: '儲存失敗，請重試',
    loadImageFailed: '載入最新影像失敗',
    clickHint: '點擊 {label} 的邊角（3~8 點，建議 4~5，完全貼著該板鋁框）— 已點 {n} 點',
    allDone: '✅ 圈選完成 — 確認下方通道對應後按「儲存」，Pi 下一輪拍攝（≤10 分鐘）自動套用',
    needTwo: '⚠️ 至少要圈選兩片板才能儲存（不在畫面中的板可按「跳過」）',
    saved: '✅ 已儲存！Pi 下一輪拍攝起生效。',
    undo: '↩ 上一步',
    reset: '🗑 重來',
    saving: '儲存中...',
    save: '💾 儲存',
    finishPanel: '✔ 完成此板（{label}）',
    skipPanel: '⏭ 此板不在畫面中，跳過（{label}）',
    jsonSummary: '座標 JSON（1920×1080）+ 通道對應',
  },
}
